/**
 * Profile › Imputación: a one-line summary that links to /imputacion.
 *
 * It reads GET /imputacion/me/summary only, which is built from the person's
 * last own-login snapshot and the calendar and never calls Redmine — opening
 * the profile must not cost a Redmine login.
 */
import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import styled from 'styled-components'
import { api } from '../../../services/api'
import type { ImputacionSummary, ImputacionTotalsLite } from '../../../types/imputacion'
import { fmtH, monthName } from './imputacionLogic'

const Card = styled.div`
  margin: ${({ theme }) => theme.spacing.xl} 0 ${({ theme }) => theme.spacing.md};
  padding: ${({ theme }) => theme.spacing.md} ${({ theme }) => theme.spacing.lg};
  border: 1px solid #90CAF9;
  border-radius: ${({ theme }) => theme.borderRadius.md};
  background: linear-gradient(135deg, #E3F2FD, #fff);
  font-size: ${({ theme }) => theme.fontSizes.sm};

  a {
    color: ${({ theme }) => theme.colors.primary};
    font-weight: 700;
  }
`

const Foot = styled.div`
  margin-top: 4px;
  font-size: ${({ theme }) => theme.fontSizes.xs};
  color: ${({ theme }) => theme.colors.textMuted};
`

/** "hace 5 minutos" / "hace 3 horas" / "hace 2 días". */
export function relativeAge(iso: string, now: number = Date.now()): string {
    const ms = now - new Date(iso).getTime()
    if (!Number.isFinite(ms) || ms < 60_000) return 'hace un momento'
    const min = Math.floor(ms / 60_000)
    if (min < 60) return `hace ${min} ${min === 1 ? 'minuto' : 'minutos'}`
    const h = Math.floor(min / 60)
    if (h < 24) return `hace ${h} ${h === 1 ? 'hora' : 'horas'}`
    const d = Math.floor(h / 24)
    return `hace ${d} ${d === 1 ? 'día' : 'días'}`
}

const part = (t: ImputacionTotalsLite) =>
    `${monthName(t.month)}: ${t.missing_to_date > 0.5 ? `te faltan ${fmtH(t.missing_to_date)}` : 'al día'}`

export function ImputacionLinkCard() {
    const [summary, setSummary] = useState<ImputacionSummary | null>(null)
    const [failed, setFailed] = useState(false)

    useEffect(() => {
        let alive = true
        api.imputacion
            .getMySummary()
            .then((r) => alive && setSummary(r.data))
            .catch(() => alive && setFailed(true))
        return () => {
            alive = false
        }
    }, [])

    const parts = summary ? [summary.previous, summary.current].filter((x): x is ImputacionTotalsLite => Boolean(x)) : []
    const hasSnapshot = Boolean(summary?.snapshot_at)

    return (
        <Card>
            <span aria-hidden="true">⏱️ </span>
            <strong>Imputación</strong>
            {hasSnapshot && parts.length > 0 ? (
                <>
                    {' — '}
                    {parts.map(part).join(' · ')}
                    {' · '}
                    <Link to="/imputacion">Ir a Imputación →</Link>
                    <Foot>según tu última visita a Imputación ({relativeAge(summary!.snapshot_at!)})</Foot>
                </>
            ) : (
                <>
                    {' — '}
                    {failed || !summary ? '' : 'Abre Imputación para ver cuántas horas te faltan · '}
                    <Link to="/imputacion">{failed || !summary ? 'Ir a Imputación →' : 'Abrir Imputación →'}</Link>
                </>
            )}
        </Card>
    )
}
